import { t } from "@redotech/redo-i18n";
import RefreshCcwIcon from "@redotech/redo-icons/refresh-ccw-01_filled.svg";
import { CompensationMethod } from "@redotech/redo-return-core-common/compensation-method";
import { StepId as ModelStepId } from "@redotech/redo-return-core-common/return-flow-answers";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { Flex } from "@redotech/redo-web/flex";
import { BlockLayout } from "@redotech/redo-web/flowchart";
import { Text } from "@redotech/redo-web/text";
import { produce } from "immer";
import * as React from "react";
import { StepType, StepTypeDetailsProps } from "../step";

function getCompensationMethodLabel(method: CompensationMethod): string {
  switch (method) {
    case CompensationMethod.REFUND:
      return t("Refund");
    case CompensationMethod.STORE_CREDIT:
      return t("Store credit");
    case CompensationMethod.EXCHANGE:
      return t("Exchange");
  }
}

const COMPENSATION_METHOD_BADGE_COLORS: Record<
  CompensationMethod,
  "blue" | "pink" | "purple"
> = {
  [CompensationMethod.REFUND]: "blue",
  [CompensationMethod.STORE_CREDIT]: "pink",
  [CompensationMethod.EXCHANGE]: "purple",
};

/**
 * Editor-internal symbol for the Compensation step. Lives in this file rather
 * than in `redo/model/src/return-flow.ts` because the persisted shape is
 * actually an AdvancedFlow CompensationStep — this symbol only exists so the
 * editor can dispatch on it via `modelToSteps`. The shell component is
 * responsible for translating AdvancedFlow steps into this model shape on
 * load and back again on save.
 */
export const COMPENSATION_STEP = Symbol("compensation_step");

export interface CompensationStepModel {
  type: typeof COMPENSATION_STEP;
  customTitle?: string;
  /**
   * Kept on the model for back-compat with existing AdvancedFlow documents.
   * The editor treats Issue compensation as terminal — see `downstream` /
   * `maxDownstream` below — so save always writes `null` here.
   */
  next: ModelStepId | null;
  compensationMethods?: CompensationMethod[];
  notifyCustomer?: boolean;
  customerNotes?: string;
}

export interface CompensationState {
  customTitle?: string;
  compensationMethods: CompensationMethod[];
  notifyCustomer: boolean;
  customerNotes?: string;
}

export const COMPENSATION = (
  Details: React.ComponentType<StepTypeDetailsProps<CompensationState>>,
): StepType<CompensationState, CompensationStepModel> => ({
  Details: (props) => React.createElement(Details, props),
  get title() {
    return t("Issue compensation");
  },
  customTitle: (state) => state.customTitle,
  description: (state) => {
    const methods = state.compensationMethods;
    if (methods.length === 0) {
      return t("No methods selected");
    }
    return t("{{count}} methods", { count: methods.length });
  },
  descriptionContent: (state) => {
    if (state.compensationMethods.length === 0) {
      return null;
    }
    return (
      <Flex dir="column" gap="sm">
        <Flex dir="column" gap="xs">
          <Text fontSize="xs" textColor="secondary">
            {t("Compensation method:")}
          </Text>
          {/* design-allow: preserve the pre-localization compensation layout */}
          <Flex dir="row" gap="xs" style={{ flexWrap: "wrap" }}>
            {state.compensationMethods.map((method) => (
              <RedoBadge
                color={COMPENSATION_METHOD_BADGE_COLORS[method]}
                key={method}
                size="xs"
                text={getCompensationMethodLabel(method)}
              />
            ))}
          </Flex>
        </Flex>
      </Flex>
    );
  },
  Icon: RefreshCcwIcon,
  iconColor: "success",
  empty: { compensationMethods: [], notifyCustomer: true },
  // Issue compensation is a terminal step: each phase's IC ends the phase's
  // sub-flow. Cross-phase transitions happen via the next phase's anchor.
  downstream: () => [],
  maxDownstream: () => 0,
  layout: () => BlockLayout.FULL,
  fromModel(model) {
    return {
      customTitle: model.customTitle,
      compensationMethods: model.compensationMethods ?? [],
      notifyCustomer: model.notifyCustomer ?? true,
      customerNotes: model.customerNotes ?? undefined,
    };
  },
  toModel(state) {
    return {
      type: COMPENSATION_STEP,
      customTitle: state.customTitle,
      next: null,
      compensationMethods: state.compensationMethods,
      notifyCustomer: state.notifyCustomer,
      customerNotes: state.customerNotes,
    };
  },
  stepDeleted(state) {
    return produce(state, () => {});
  },
  valid(state) {
    return state.compensationMethods.length > 0;
  },
});
